// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Stepper } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import type { TriageStage } from '@polarissleep/scheduling';
import type { JSX } from 'react';

/** The 3 high-level care journey phases displayed in the stepper */
const JOURNEY_PHASES = [
  { label: 'Screener Review', description: 'Determine eligibility' },
  { label: 'Testing & Diagnosis', description: 'Define the condition' },
  { label: 'Treatment & Follow-up', description: 'Explore options' },
] as const;

/**
 * Maps a triage stage to the active stepper phase index.
 * Phase 0 = Screener Review (intake)
 * Phase 1 = Testing & Diagnosis (hsat, hsat-results)
 * Phase 2 = Treatment & Follow-up (treatment-setup, followup-*, annual-followup, non-adherent)
 * @param triageStage - The patient's current triage stage
 * @returns The active phase index (0, 1, or 2)
 */
function getActivePhase(triageStage: TriageStage | undefined): number {
  if (!triageStage) {
    return 0;
  }

  switch (triageStage) {
    case 'intake':
      return 0;
    case 'hsat':
    case 'hsat-results':
      return 1;
    case 'treatment-setup':
    case 'followup-30':
    case 'followup-90':
    case 'followup-180':
    case 'annual-followup':
    case 'non-adherent':
      return 2;
    default:
      return 0;
  }
}

interface CareJourneyStepperProps {
  triageStage: TriageStage | undefined;
}

/**
 * Displays a patient's care journey as a 3-step stepper in the provider-app
 * Appointments tab. Active step is derived from the patient's current triage
 * stage. Responsive: horizontal on desktop, vertical on mobile.
 *
 * Copy is third-person (clinician viewpoint) to match the provider context.
 * @param root0 - Component props
 * @param root0.triageStage - The patient's current triage stage
 * @returns The care journey stepper component
 */
export function CareJourneyStepper({ triageStage }: CareJourneyStepperProps): JSX.Element {
  const isSmallScreen = useMediaQuery('(max-width: 876px)');
  const activePhase = getActivePhase(triageStage);

  return (
    <Box p="lg">
      <Stepper
        active={activePhase}
        orientation={isSmallScreen ? 'vertical' : 'horizontal'}
        styles={{ stepLabel: { fontWeight: 700 } }}
      >
        {JOURNEY_PHASES.map((phase) => (
          <Stepper.Step key={phase.label} label={phase.label} description={phase.description} />
        ))}
      </Stepper>
    </Box>
  );
}
